import Chip from '@mui/material/Chip';

export type StatusType =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'WAITING'
  | 'BLOCKED'
  | 'READY'
  | 'STORES_PICKING'
  | 'READY_FOR_PICKUP'
  | 'IN_PROGRESS'
  | 'WORK_COMPLETE'
  | 'PAUSED'
  | 'INSPECTION_PENDING'
  | 'INSPECTION_FAILED'
  | 'INSPECTION_PASSED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'CLOSED'
  | 'BLOCKED_DEPENDENCY'
  | 'BLOCKED_SHORTAGE'
  | 'ON_HOLD';

export type PriorityType = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

interface StatusConfig {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

const STATUS_CONFIG: Record<StatusType, StatusConfig> = {
  DRAFT: { label: 'Draft', color: 'default' },
  SCHEDULED: { label: 'Scheduled', color: 'info' },
  WAITING: { label: 'Waiting', color: 'warning' },
  BLOCKED: { label: 'Blocked', color: 'error' },
  READY: { label: 'Ready', color: 'info' },
  STORES_PICKING: { label: 'Stores Picking', color: 'secondary' },
  READY_FOR_PICKUP: { label: 'Ready for Pickup', color: 'warning' },
  IN_PROGRESS: { label: 'In Progress', color: 'primary' },
  WORK_COMPLETE: { label: 'Work Complete', color: 'success' },
  PAUSED: { label: 'Paused', color: 'warning' },
  INSPECTION_PENDING: { label: 'Inspection Pending', color: 'warning' },
  INSPECTION_FAILED: { label: 'Inspection Failed', color: 'error' },
  INSPECTION_PASSED: { label: 'Inspection Passed', color: 'success' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'default' },
  PAYMENT_PENDING: { label: 'Payment Pending', color: 'warning' },
  PAID: { label: 'Paid', color: 'success' },
  CLOSED: { label: 'Closed', color: 'default' },
  BLOCKED_DEPENDENCY: { label: 'Blocked (Dependency)', color: 'error' },
  BLOCKED_SHORTAGE: { label: 'Blocked (Shortage)', color: 'error' },
  ON_HOLD: { label: 'On Hold', color: 'warning' },
};

const PRIORITY_CONFIG: Record<PriorityType, StatusConfig> = {
  LOW: { label: 'Low', color: 'info' },
  MEDIUM: { label: 'Medium', color: 'primary' },
  HIGH: { label: 'High', color: 'warning' },
  URGENT: { label: 'Urgent', color: 'error' },
};

export interface StatusBadgeProps {
  status: StatusType | PriorityType | string;
  type?: 'status' | 'priority' | 'custom';
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
  customColor?: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
}

export default function StatusBadge({
  status,
  type = 'status',
  variant = 'filled',
  size = 'small',
  customColor,
}: StatusBadgeProps) {
  let config: StatusConfig;

  if (type === 'custom') {
    config = {
      label: status,
      color: customColor || 'default',
    };
  } else if (type === 'priority') {
    config = PRIORITY_CONFIG[status as PriorityType] || { label: status, color: 'default' };
  } else {
    config = STATUS_CONFIG[status as StatusType] || { label: status, color: 'default' };
  }

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      variant={variant}
      sx={config.color === 'info' && variant === 'filled'
        ? { bgcolor: '#01579b', color: 'common.white' }
        : undefined}
    />
  );
}
